import React, { useState, useEffect, useMemo } from "react";
import api from "./api";
import useAuth from "../hooks/useAuth";

/* Body part key → stype string (must match PART_TO_STYPE in ConsultationIpadView) */
const PART_TO_STYPE = {
  cervical:     "Cervical (Neck)",
  upperback:    "UpperBack",
  middleback:   "MiddleBack",
  lowerback:    "LowerBack & Pelvis",
  chest:        "Chest",
  heart:        "Heart",
  digestive:    "Digestive",
  abdomen:      "Abdomen",
  renal:        "Renal",
  reproductive: "Reproductive",
  coccyx:       "Coccyx",
  shoulder_L:   "Shoulder", shoulder_R:  "Shoulder",
  shoulder_LB:  "Shoulder", shoulder_RB: "Shoulder",
  arm_L:        "Arm",      arm_R:       "Arm",
  wrist_L:      "Wrist",    wrist_R:     "Wrist",
  knee_L:       "Knee",     knee_R:      "Knee",
  thigh_L:      "Thigh",    thigh_R:     "Thigh",
  leg_L:        "Leg",      leg_R:       "Leg",
  calf_L:       "Calf",     calf_R:      "Calf",
  ankle_L:      "Ankle",    ankle_R:     "Ankle",
  foot_L:       "Foot",     foot_R:      "Foot",
  lung:         "Lungs",
};

const PART_LABELS = {
  cervical: "Cervical / Neck", upperback: "Upper Back", middleback: "Middle Back",
  lowerback: "Lower Back", chest: "Chest", heart: "Heart", digestive: "Digestive",
  abdomen: "Abdomen", renal: "Renal", reproductive: "Reproductive", coccyx: "Coccyx",
  lung: "Lungs", shoulder_L: "Shoulder (L)", shoulder_R: "Shoulder (R)",
  arm_L: "Arm (L)", arm_R: "Arm (R)", wrist_L: "Wrist (L)", wrist_R: "Wrist (R)",
  knee_L: "Knee (L)", knee_R: "Knee (R)", thigh_L: "Thigh (L)", thigh_R: "Thigh (R)",
  leg_L: "Leg (L)", leg_R: "Leg (R)", calf_L: "Calf (L)", calf_R: "Calf (R)",
  ankle_L: "Ankle (L)", ankle_R: "Ankle (R)", foot_L: "Foot (L)", foot_R: "Foot (R)",
};

/* Part accent colors for badges */
const PART_COLORS = [
  "#4f46e5","#0891b2","#16a34a","#d97706","#dc2626",
  "#7c3aed","#0d9488","#b45309","#4338ca","#059669",
];

export default function TreatmentPlanModal({
  patientCard,
  patientName,
  bodySymptoms,
  selectedSeqs: initSeqs,
  selectedRecomms: initRecomms,
  partComments,
  generalComments,
  prevConsultParts,
  prevBodySymptoms,
  existingPlan,
  existingPlanPartHolds,
  existingPlanExcludedParts,
  existingSuggestedServices,
  existingPlanRomLinkedSelections,
  gtpPlans: gtpPlansProp,
  completedSessionsCount,
  onClose,
  onSubmit,
}) {
  const gtpPlans = gtpPlansProp || [];
  const { auth } = useAuth();
  const [mapping, setMapping]       = useState(null);
  const [loadingMap, setLoadingMap] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Hold feature — available to all staff who can open this modal
  const canHold = !!(auth?.roles?.length);

  /* ── Exercise selection state ────────────────────────────────────────────── */
  // allPartSeqs: { partKey: [{ name, duration, repetition, stype, ... }] } from mapping
  const [allPartSeqs,       setAllPartSeqs]       = useState({});
  // selectedEx: Set<string> of selected exercise names (null = loading)
  const [selectedEx,        setSelectedEx]        = useState(null);
  // seqEdits: { seqName: { duration, repetition } } — doctor overrides for common exercises
  const [seqEdits,          setSeqEdits]          = useState({});
  // Individual hold state (for single exercises)
  const [holdData,          setHoldData]          = useState({});
  const [holdForm,          setHoldForm]          = useState(null);
  // Part-level hold: { partKey: { held: bool, restartDate: string } }
  // Initialise from existing consultation so re-opening modal preserves hold state
  const [partHoldState,     setPartHoldState]     = useState(existingPlanPartHolds || {});
  const [showPartHoldForm,  setShowPartHoldForm]  = useState(null); // partKey
  // Parts explicitly removed from the plan by the therapist
  const [excludedParts,    setExcludedParts]     = useState(new Set(existingPlanExcludedParts || []));
  // ROM linked parts: bodyPartsConfig + doctor selections
  const [bpConfig,              setBpConfig]              = useState({ front: [], back: [] });
  const [romLinkedSelections,   setRomLinkedSelections]   = useState(existingPlanRomLinkedSelections || {});

  /* ── Suggested services ─────────────────────────────────────────────── */
  const [allServices,     setAllServices]     = useState([]);
  const [planServices,    setPlanServices]    = useState(() => {
    // Add-on GTP: start with empty services — only new services for THIS GTP
    if (existingPlan) return [];
    // First GTP: dedup existing services by name
    const seen = new Set();
    return (existingSuggestedServices || []).filter(s => {
      const k = (s.name || '').toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  });
  const [showSvcPicker,   setShowSvcPicker]   = useState(false);
  const [svcSearch,       setSvcSearch]       = useState('');
  const [expandedGtps,    setExpandedGtps]    = useState(new Set());

  /* ── Recommendations + session counts + comments ──────────────────────── */
  const [planRecomms, setPlanRecomms] = useState(initRecomms || []);
  const [recHolds,    setRecHolds]    = useState({});
  // Only suggest sessions when there are active body parts — avoid spurious defaults on empty consultations
  const _hasBodyParts = Object.keys(bodySymptoms || {}).some(
    k => Object.values(bodySymptoms[k] || {}).some(v => v > 0)
  );
  // When existingPlan present these are ADD-ONs; otherwise they are the full counts
  const [mythsessions, setMyth]       = useState(existingPlan ? 0 : (_hasBodyParts ? 12 : 0));
  const [maintsessions, setMaint]     = useState(existingPlan ? 0 : (_hasBodyParts ? 4 : 0));
  const [preventivesessions, setPreventive] = useState(existingPlan ? 0 : (_hasBodyParts ? 2 : 0));
  const [drcomments, setDrcomments]   = useState(existingPlan?.drcomments || "");
  const [selectedOrgs, setSelectedOrgs] = useState([]); // empty = all orgs shown
  const [consultMode, setConsultMode] = useState('doctor'); // 'review' | 'doctor' | 'deepak'
  const isReview    = consultMode === 'review';
  const consultDoctor = consultMode === 'deepak' ? 'deepak' : 'doctor';

  const activeParts = Object.keys(bodySymptoms || {}).filter(
    k => (bodySymptoms[k]?.pain || 0) > 0 || Object.values(bodySymptoms[k] || {}).some(v => v > 0)
  );

  /* ── ROM linked parts lookup ─────────────────────────────────────────────── */
  const allBpAnnotations = useMemo(() => [...bpConfig.front, ...bpConfig.back], [bpConfig]);
  const bpLookup = useMemo(() => {
    const m = {};
    allBpAnnotations.forEach(a => { m[a.key] = a; });
    return m;
  }, [allBpAnnotations]);
  // Active parts that are ROM-type and have linked parts configured
  const romActiveParts = useMemo(() =>
    activeParts.filter(pk =>
      !excludedParts.has(pk) &&
      bpLookup[pk]?.metricType === 'ROM' &&
      (bpLookup[pk]?.romLinkedParts?.length || 0) > 0
    ),
  [activeParts, excludedParts, bpLookup]);

  // Auto-select all linked parts for new ROM parts that don't have existing selections
  useEffect(() => {
    if (!romActiveParts.length) return;
    setRomLinkedSelections(prev => {
      const next = { ...prev };
      let changed = false;
      romActiveParts.forEach(pk => {
        if (!(pk in next)) {
          next[pk] = [...(bpLookup[pk]?.romLinkedParts || [])];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [romActiveParts.join(',')]);

  /* ── Load mapping + build allPartSeqs ───────────────────────────────────── */
  /* ── Fetch all master services once ─────────────────────────────────────── */
  useEffect(() => {
    api.get(`${process.env.REACT_APP_URL}/services`)
      .then(r => setAllServices(r.data || []))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Fetch body-parts config for ROM/MVC/linked-parts info ─────────────── */
  useEffect(() => {
    api.get(`${process.env.REACT_APP_URL}/bodyparts`)
      .then(r => { if (r.data) setBpConfig({ front: r.data.front || [], back: r.data.back || [] }); })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Auto-populate 3 session-linked services when services load ──────────── */
  useEffect(() => {
    if (!allServices.length) return;
    const SESSION_TYPES = [
      { label: 'Session with Doctor',           qty: Number(mythsessions) || 0 },
      { label: 'Session with Senior Therapist', qty: Number(maintsessions) || 0 },
      { label: 'Session with Therapist',        qty: Number(preventivesessions) || 0 },
    ];
    setPlanServices(prev => {
      // Enrich existing services with _masterQty from master data (for package detection)
      const enriched = prev.map(s => {
        if (s._masterQty != null) return s;
        const master = allServices.find(m => (m.name || '').toLowerCase() === (s.name || '').toLowerCase());
        return master ? { ...s, _masterQty: master.qty } : s;
      });
      const updated = [...enriched];
      SESSION_TYPES.forEach(st => {
        const alreadyIn = updated.some(s => (s.name || '').toLowerCase() === st.label.toLowerCase());
        if (alreadyIn) return;
        const masterSvc = allServices.find(s => s.name.toLowerCase() === st.label.toLowerCase());
        if (masterSvc) updated.push({ ...masterSvc, name: st.label, qty: st.qty, amount: masterSvc.amount || 0 });
      });
      return updated;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allServices]);

  /* ── Service helpers ─────────────────────────────────────────────────────── */
  function updateSvcQty(idx, qty) {
    setPlanServices(prev => prev.map((s, i) => i === idx ? { ...s, qty: Number(qty) || 0 } : s));
  }
  function removeSvc(idx) {
    setPlanServices(prev => prev.filter((_, i) => i !== idx));
  }
  function addSvc(svc) {
    setPlanServices(prev => {
      if (prev.some(s => s.name === svc.name)) return prev;
      return [...prev, { ...svc, _masterQty: svc.qty, qty: 1 }];
    });
    setSvcSearch('');
    setShowSvcPicker(false);
  }
  const _isPkg = (svc) => /package/i.test(svc.name || '') && Number(svc._masterQty || 0) > 1;

  /* ── Mapping + initSeqs load ─────────────────────────────────────────────── */
  useEffect(() => {
    api.get(`${process.env.REACT_APP_URL}/patients/consultation/treatment-mapping`)
      .then(r => {
        setMapping(r.data);

        /* Session counts from mapping averages */
        const sm = r.data?.avgSessions || {};
        let maxAvg = 0;
        activeParts.forEach(p => {
          const base = p.replace(/_L$|_R$|_LB$|_RB$/, "");
          if (sm[base] && sm[base] > maxAvg) maxAvg = sm[base];
        });
        // Only override session defaults for a fresh plan (not when adding on top of existing)
        if (maxAvg > 0 && !existingPlan) {
          setMyth(Math.max(8, Math.round(maxAvg * 0.6)));
          setMaint(Math.max(2, Math.round(maxAvg * 0.25)));
          setPreventive(Math.max(1, Math.round(maxAvg * 0.15)));
        }

        /* Build allPartSeqs: ALL sequences per body part (no limit) */
        const partSeqMap = {};
        const stypeToPartKeys = {};
        const addStypeKey = (key, partKey) => {
          if (!key) return;
          if (!stypeToPartKeys[key]) stypeToPartKeys[key] = [];
          if (!stypeToPartKeys[key].includes(partKey)) stypeToPartKeys[key].push(partKey);
        };

        activeParts.forEach(partKey => {
          const stype = PART_TO_STYPE[partKey];
          const mk1 = stype ? stype.toLowerCase() : null;
          const mk2 = partKey.toLowerCase();
          const mk3 = partKey.replace(/_[LR]$|_[LR]B$/, '').toLowerCase();
          addStypeKey(mk1, partKey);
          addStypeKey(mk2, partKey);
          if (mk3 !== mk2) addStypeKey(mk3, partKey);

          const seqList = (mk1 && r.data?.sequences?.[mk1])
            || r.data?.sequences?.[mk2]
            || r.data?.sequences?.[mk3]
            || [];
          partSeqMap[partKey] = seqList.map(s => ({ ...s, patientCard, status: "notStarted" }));
        });

        // Distribute initSeqs to matching parts: prefer sourcePart, then stype match, then first active part
        (initSeqs || []).forEach(s => {
          // 1. If exercise has sourcePart and it's in activeParts, place it there
          if (s.sourcePart && activeParts.includes(s.sourcePart)) {
            if (!partSeqMap[s.sourcePart]) partSeqMap[s.sourcePart] = [];
            if (!partSeqMap[s.sourcePart].some(e => e.name === s.name)) {
              partSeqMap[s.sourcePart].push({ ...s, patientCard, status: s.status || "notStarted" });
            }
            return;
          }
          // 2. Fall back to stype matching (handles L/R pairs sharing the same stype)
          const stypeRaw = Array.isArray(s.stype) ? (s.stype[0] || '') : (s.stype || '');
          const stLower  = stypeRaw.toLowerCase().trim();
          const targets  = (stypeToPartKeys[stLower] || []).filter(p => activeParts.includes(p));
          const parts    = targets.length > 0 ? targets : (activeParts.length > 0 ? [activeParts[0]] : []);
          parts.forEach(targetPart => {
            if (!partSeqMap[targetPart]) partSeqMap[targetPart] = [];
            if (!partSeqMap[targetPart].some(e => e.name === s.name)) {
              partSeqMap[targetPart].push({ ...s, patientCard, status: s.status || "notStarted" });
            }
          });
        });

        setAllPartSeqs(partSeqMap);

        /* Auto-populate recommendations if none yet */
        if ((initRecomms || []).length === 0) {
          const suggestedRecs = [];
          const recTexts = new Set();
          activeParts.forEach(partKey => {
            const stype = PART_TO_STYPE[partKey];
            if (!stype) return;
            const mapKey = stype.toLowerCase();
            const recList = r.data?.recommendations?.[mapKey] || [];
            recList.slice(0, 3).forEach(rec => {
              if (!recTexts.has(rec.name)) {
                recTexts.add(rec.name);
                suggestedRecs.push({ recommendation: rec.name, qty: rec.qty, not: rec.not, nod: rec.nod, rtype: rec.rtype, patientCard });
              }
            });
          });
          if (suggestedRecs.length > 0) setPlanRecomms(suggestedRecs);
        }

        /* Initialize selectedEx */
        if ((initSeqs || []).length > 0) {
          // Editing existing plan: pre-select existing sequences
          const existingNames = new Set((initSeqs || []).map(s => s.name));
          setSelectedEx(existingNames);
          // Pre-populate seqEdits from initSeqs (custom durations/reps)
          const edits = {};
          (initSeqs || []).forEach(s => {
            if (s.name) edits[s.name] = { duration: s.duration, repetition: s.repetition || 1 };
          });
          setSeqEdits(edits);
          // Pre-populate holdData from initSeqs
          const hd = {};
          (initSeqs || []).forEach(s => {
            if (s.held && s.name) hd[s.name] = { heldAt: s.heldAt || new Date().toISOString(), restartDate: s.heldRestartDate || null };
          });
          setHoldData(hd);
        } else {
          // New plan: select ALL exercises across all parts
          const allNames = new Set();
          Object.values(partSeqMap).forEach(seqs => seqs.forEach(s => allNames.add(s.name)));
          setSelectedEx(allNames);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingMap(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Derived: seqName → [partKeys] and common exercises ─────────────────── */
  const seqPartsMap = useMemo(() => {
    const m = {};
    Object.entries(allPartSeqs).forEach(([partKey, seqs]) => {
      seqs.forEach(s => {
        if (!m[s.name]) m[s.name] = [];
        if (!m[s.name].includes(partKey)) m[s.name].push(partKey);
      });
    });
    return m;
  }, [allPartSeqs]);

  const commonSeqNames = useMemo(() =>
    new Set(Object.entries(seqPartsMap).filter(([, parts]) => parts.length > 1).map(([n]) => n)),
  [seqPartsMap]);

  /* ── All unique sequences across all parts ───────────────────────────────── */
  const allUniqueSeqs = useMemo(() => {
    const seen = new Set();
    const result = [];
    Object.values(allPartSeqs).flat().forEach(s => {
      if (!seen.has(s.name)) { seen.add(s.name); result.push(s); }
    });
    return result;
  }, [allPartSeqs]);

  /* ── Initial exercise map — for NEW / EDITED / REMOVED detection ─────────── */
  const initMap = useMemo(() => {
    const m = {};
    (initSeqs || []).forEach(s => { if (s.name) m[s.name] = s; });
    return m;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Helpers ─────────────────────────────────────────────────────────────── */
  function toggleEx(name) {
    setSelectedEx(prev => {
      const s = new Set(prev);
      if (s.has(name)) s.delete(name); else s.add(name);
      return s;
    });
  }

  function isHeld(name) { return !!holdData[name]; }

  function holdSeq(name, restartDate) {
    setHoldData(prev => ({ ...prev, [name]: { heldAt: new Date().toISOString(), restartDate: restartDate || null } }));
    setHoldForm(null);
  }

  function unholdSeq(name) {
    setHoldData(prev => { const n = { ...prev }; delete n[name]; return n; });
  }

  function holdEntirePart(partKey, restartDate) {
    const seqs = allPartSeqs[partKey] || [];
    const heldAt = new Date().toISOString();
    setHoldData(prev => {
      const next = { ...prev };
      seqs.forEach(s => {
        if (selectedEx?.has(s.name)) next[s.name] = { heldAt, restartDate: restartDate || null };
      });
      return next;
    });
    setPartHoldState(prev => ({ ...prev, [partKey]: { held: true, restartDate: restartDate || null } }));
  }

  function excludePart(partKey) {
    setExcludedParts(prev => new Set([...prev, partKey]));
    // Deselect all exercises in this part so they don't slip into submission
    const seqsInPart = allPartSeqs[partKey] || [];
    if (seqsInPart.length > 0) {
      setSelectedEx(prev => {
        const s = new Set(prev);
        seqsInPart.forEach(seq => s.delete(seq.name));
        return s;
      });
    }
  }
  function restorePart(partKey) {
    setExcludedParts(prev => { const s = new Set(prev); s.delete(partKey); return s; });
  }

  function unholdEntirePart(partKey) {
    const seqs = allPartSeqs[partKey] || [];
    setHoldData(prev => {
      const next = { ...prev };
      seqs.forEach(s => { delete next[s.name]; });
      return next;
    });
    setPartHoldState(prev => { const n = { ...prev }; delete n[partKey]; return n; });
  }

  function updateRec(text, field, value) {
    setPlanRecomms(prev => prev.map(r =>
      (r.recommendation || r.name) === text ? { ...r, [field]: value } : r
    ));
  }
  function removeRec(text) {
    setPlanRecomms(prev => prev.filter(r => (r.recommendation || r.name) !== text));
  }
  function toggleRecHold(text) {
    setRecHolds(prev => ({ ...prev, [text]: !prev[text] }));
  }

  /* ── Submit ──────────────────────────────────────────────────────────────── */
  async function handleSubmit() {
    if (!patientCard) return;
    setSubmitting(true);
    try {
      // Build sequences from selected exercises with hold data + edits applied
      const sequencesWithHold = allUniqueSeqs
        .filter(s => selectedEx?.has(s.name))
        .map(s => {
          const edit = seqEdits[s.name];
          return {
            ...s,
            patientCard,
            status:          "notStarted",
            duration:        edit?.duration   != null ? edit.duration   : s.duration,
            repetition:      edit?.repetition != null ? edit.repetition : (s.repetition || 1),
            held:            isHeld(s.name) ? true : (s.held || false),
            heldAt:          holdData[s.name]?.heldAt          || s.heldAt          || null,
            heldRestartDate: holdData[s.name]?.restartDate      || s.heldRestartDate || null,
          };
        });

      // Map session counts to session-linked service quantities.
      // Each GTP's planServices is fresh (not carried from previous GTPs).
      const SESSION_QTY = {
        'session with doctor':           Number(mythsessions) || 0,
        'session with senior therapist': Number(maintsessions) || 0,
        'session with therapist':        Number(preventivesessions) || 0,
      };
      const finalPlanServices = planServices.map(svc => {
        const key = (svc.name || '').toLowerCase();
        if (key in SESSION_QTY) return { ...svc, qty: SESSION_QTY[key] };
        return svc;
      });

      // "Submit for Approval" (not Review) auto-adds the chosen consultation service (dedup)
      // Only on FIRST GTP — add-on GTPs shouldn't re-add a consultation charge
      if (!isReview && !existingPlan) {
        const consultSvcName = consultDoctor === 'deepak'
          ? 'Consultation with Dr. Deepak'
          : 'Consultation with Doctor';
        if (!finalPlanServices.some(s => (s.name || '').toLowerCase() === consultSvcName.toLowerCase())) {
          // Look up full service object from catalog for complete amount/tax data
          const catalogSvc = allServices.find(s => (s.name || '').toLowerCase() === consultSvcName.toLowerCase());
          if (catalogSvc) {
            finalPlanServices.push({ ...catalogSvc, qty: 1 });
          } else {
            finalPlanServices.push({ name: consultSvcName, qty: 1 });
          }
        }
      }

      // Only keep holds for parts that still have active symptoms AND are not excluded from the plan.
      // This ensures stale holds don't linger on the SVG after a part is removed or excluded.
      const activeSymptomParts = new Set(
        Object.entries(bodySymptoms || {})
          .filter(([, v]) => Object.values(v || {}).some(x => x > 0))
          .map(([k]) => k)
      );
      const cleanedPartHolds = Object.fromEntries(
        Object.entries(partHoldState).filter(
          ([pk]) => activeSymptomParts.has(pk) && !excludedParts.has(pk)
        )
      );

      await onSubmit({
        sequences:          sequencesWithHold,
        recommendations:    planRecomms.map(r => ({ ...r, held: !!(recHolds[r.recommendation || r.name]) })),
        mythsessions:       Number(mythsessions),
        maintsessions:      Number(maintsessions),
        preventivesessions: Number(preventivesessions),
        drcomments,
        planStatus:         isReview ? "reviewed" : "pending_approval",
        therapist:          auth?.username || "",
        planExcludedParts:  [...excludedParts],
        planPartHolds:      cleanedPartHolds,
        planRomLinkedSelections: romLinkedSelections,
        planServices:       finalPlanServices,
      });
    } finally {
      setSubmitting(false);
    }
  }

  /* ── Part color helper ───────────────────────────────────────────────────── */
  function partColor(partKey) {
    const idx = activeParts.indexOf(partKey);
    return PART_COLORS[idx % PART_COLORS.length];
  }

  /* ════════════════════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════════════════════ */
  return (
    <div style={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <div style={styles.title}>Auto-Generated Treatment Plan</div>
            <div style={styles.subtitle}>{patientName || patientCard} &mdash; {new Date().toLocaleDateString()}</div>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.body}>
          {loadingMap && <div style={styles.loadingMsg}>Analysing patient data…</div>}

          {/* ── Body parts summary ─────────────────────────────────────────── */}
          {activeParts.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Body Parts &amp; Patient Comments</div>
              {activeParts.map(partKey => {
                const sym = bodySymptoms[partKey] || {};
                const comment = (partComments || {})[partKey] || "";
                const pain = sym.pain || 0;
                const sympList = ["numbness","weakness","stiffness","burning","spasm","instability"]
                  .filter(k => (sym[k] || 0) > 0).map(k => `${k}: ${sym[k]}`).join(", ");
                const isNew = prevConsultParts && !prevConsultParts.has(partKey);
                const prevPain = prevBodySymptoms?.[partKey]?.pain || 0;
                const painDelta = pain - prevPain;
                const isChanged = !isNew && prevConsultParts && prevPain > 0 && Math.abs(painDelta) >= 2;
                const borderColor = isNew ? "#16a34a" : isChanged ? (painDelta > 0 ? "#dc2626" : "#2563eb") : "#e2e8f0";
                const bgColor = isNew ? "#f0fdf4" : isChanged ? (painDelta > 0 ? "#fef2f2" : "#eff6ff") : "#f8fafc";
                return (
                  <div key={partKey} style={{ marginBottom: 8, padding: "8px 10px", background: bgColor, borderRadius: 8, border: `1.5px solid ${borderColor}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: comment ? 4 : 0, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>{PART_LABELS[partKey] || partKey}</span>
                      {pain > 0 && <span style={styles.painBadge}>pain {pain}</span>}
                      {isNew && <span style={{ fontSize: 10, background: "#16a34a", color: "#fff", borderRadius: 99, padding: "2px 7px", fontWeight: 700 }}>🆕 NEW</span>}
                      {isChanged && painDelta > 0 && <span style={{ fontSize: 10, background: "#dc2626", color: "#fff", borderRadius: 99, padding: "2px 7px", fontWeight: 700 }}>↑ +{painDelta}</span>}
                      {isChanged && painDelta < 0 && <span style={{ fontSize: 10, background: "#2563eb", color: "#fff", borderRadius: 99, padding: "2px 7px", fontWeight: 700 }}>↓ {painDelta}</span>}
                      {sympList && <span style={{ fontSize: 11, color: "#888" }}>{sympList}</span>}
                    </div>
                    {comment && (
                      <div style={{ fontSize: 12, color: "#555", fontStyle: "italic", borderLeft: "3px solid #cbd5e0", paddingLeft: 8 }}>{comment}</div>
                    )}
                  </div>
                );
              })}
              {prevConsultParts && Array.from(prevConsultParts).filter(k => !bodySymptoms[k] || !(Object.values(bodySymptoms[k] || {}).some(v => v > 0))).length > 0 && (
                <div style={{ marginBottom: 6, display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {Array.from(prevConsultParts).filter(k => !bodySymptoms[k] || !(Object.values(bodySymptoms[k] || {}).some(v => v > 0))).map(k => (
                    <span key={k} style={{ fontSize: 10, background: "#f3f4f6", color: "#9ca3af", borderRadius: 99, padding: "2px 8px", border: "1px solid #e5e7eb", textDecoration: "line-through" }}>
                      {PART_LABELS[k] || k}
                    </span>
                  ))}
                </div>
              )}
              {generalComments && (
                <div style={{ marginTop: 6, padding: "8px 10px", background: "#fffbeb", borderRadius: 8, border: "1px solid #fde68a" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>General Comments</div>
                  <div style={{ fontSize: 12, color: "#555" }}>{generalComments}</div>
                </div>
              )}
            </div>
          )}

          {/* ── Exercises by Body Part (unified badge-select view) ─────────── */}
          <div style={styles.section}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
              <div style={styles.sectionTitle}>Exercises by Body Part</div>
              {selectedEx && (
                <>
                  <span style={{ fontSize: 11, color: "#6b7280" }}>
                    <strong>{selectedEx.size}</strong> of {allUniqueSeqs.length} selected
                  </span>
                  <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#374151", cursor: "pointer", userSelect: "none" }}>
                    <input
                      type="checkbox"
                      style={{ cursor: "pointer", width: 14, height: 14 }}
                      checked={allUniqueSeqs.length > 0 && selectedEx.size === allUniqueSeqs.length}
                      ref={el => { if (el) el.indeterminate = selectedEx.size > 0 && selectedEx.size < allUniqueSeqs.length; }}
                      onChange={e => {
                        if (e.target.checked) setSelectedEx(new Set(allUniqueSeqs.map(s => s.name)));
                        else setSelectedEx(new Set());
                      }}
                    />
                    Select all
                  </label>
                </>
              )}
              {commonSeqNames.size > 0 && (
                <span style={{ fontSize: 11, background: "#fef3c7", color: "#92400e", borderRadius: 99, padding: "2px 9px", border: "1px solid #fde68a", fontWeight: 600, marginLeft: "auto" }}>
                  ⚠️ {commonSeqNames.size} shared across parts
                </span>
              )}
            </div>

            {loadingMap && <div style={{ fontSize: 12, color: "#888", fontStyle: "italic" }}>Loading exercises…</div>}

            {!loadingMap && activeParts.length === 0 && (
              <div style={styles.emptyMsg}>No active body parts selected.</div>
            )}

            {!loadingMap && activeParts.map(partKey => {
              const seqs        = allPartSeqs[partKey] || [];
              const isExcluded  = excludedParts.has(partKey);
              const color       = partColor(partKey);
              const pain        = bodySymptoms[partKey]?.pain || 0;
              const isPartHeld  = !!partHoldState[partKey]?.held;
              const showPartHF  = showPartHoldForm === partKey;
              const partRestart = partHoldState[partKey]?.restartDate || '';
              const selectedInPart = seqs.filter(s => selectedEx?.has(s.name)).length;

              return (
                <div key={partKey} style={{
                  marginBottom: 14,
                  padding: "10px 12px",
                  border: isExcluded ? "1.5px dashed #fecaca"
                        : isPartHeld ? "1.5px solid #fde68a"
                        : seqs.length === 0 ? "1.5px dashed #d1d5db"
                        : `1.5px solid ${color}22`,
                  borderRadius: 10,
                  background: isExcluded ? "#fff5f5"
                            : isPartHeld ? "#fffbeb"
                            : `${color}08`,
                  opacity: isExcluded ? 0.75 : 1,
                }}>
                  {/* Part header */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: isExcluded ? 0 : 6, flexWrap: "wrap" }}>
                    <span style={{
                      fontSize: 13, fontWeight: 700,
                      color: isExcluded ? "#ef4444" : isPartHeld ? "#92400e" : color,
                      textDecoration: isExcluded ? "line-through" : "none",
                    }}>
                      {isPartHeld && !isExcluded ? "🔒 " : ""}
                      {PART_LABELS[partKey] || partKey}
                    </span>
                    {!isExcluded && prevConsultParts && !prevConsultParts.has(partKey) && (
                      <span style={{ fontSize: 10, background: "#dcfce7", color: "#16a34a", borderRadius: 99, padding: "1px 7px", border: "1px solid #bbf7d0", fontWeight: 700 }}>NEW</span>
                    )}
                    {!isExcluded && pain > 0 && <span style={styles.painBadge}>pain {pain}</span>}
                    {!isExcluded && seqs.length > 0 && (
                      <span style={{ fontSize: 11, color: "#9ca3af" }}>{selectedInPart}/{seqs.length} selected</span>
                    )}
                    {isExcluded && (
                      <span style={{ fontSize: 11, color: "#ef4444", fontStyle: "italic" }}>removed from plan</span>
                    )}

                    <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                      {!isExcluded && canHold && !isPartHeld && (
                        <button
                          title="Put entire part on hold"
                          style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, border: "1px solid #fcd34d", background: "#fffbeb", color: "#92400e", cursor: "pointer" }}
                          onClick={() => setShowPartHoldForm(showPartHF ? null : partKey)}
                        >🔒 Hold</button>
                      )}
                      {!isExcluded && canHold && isPartHeld && (
                        <button
                          title="Unhold this part"
                          style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#16a34a", cursor: "pointer" }}
                          onClick={() => unholdEntirePart(partKey)}
                        >🔓 Unhold</button>
                      )}
                      {!isExcluded ? (
                        <button
                          title="Remove this part from the plan"
                          style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, border: "1px solid #fecaca", background: "#fff5f5", color: "#dc2626", cursor: "pointer" }}
                          onClick={() => excludePart(partKey)}
                        >✕ Remove</button>
                      ) : (
                        <button
                          title="Restore this part to the plan"
                          style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#16a34a", cursor: "pointer" }}
                          onClick={() => restorePart(partKey)}
                        >↩ Restore</button>
                      )}
                    </div>
                  </div>

                  {/* Part-level hold form — hidden when part is excluded */}
                  {!isExcluded && showPartHF && (
                    <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 8px", background: "#fffbeb", borderRadius: 8, border: "1px solid #fde68a", marginBottom: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, color: "#92400e", fontWeight: 600 }}>
                        Hold all {selectedInPart} selected exercises in {PART_LABELS[partKey] || partKey} from today
                      </span>
                      <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
                        Restart (optional):
                        <input type="date"
                          value={partRestart}
                          style={{ fontSize: 11, border: "1px solid #fcd34d", borderRadius: 4, padding: "2px 4px" }}
                          onChange={e => setPartHoldState(prev => ({ ...prev, [partKey]: { ...prev[partKey], restartDate: e.target.value } }))}
                        />
                      </label>
                      <button
                        style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, border: "none", background: "#f59e0b", color: "#fff", cursor: "pointer", fontWeight: 700 }}
                        onClick={() => { holdEntirePart(partKey, partRestart || null); setShowPartHoldForm(null); }}
                      >✓ Confirm Hold</button>
                      <button
                        style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer" }}
                        onClick={() => setShowPartHoldForm(null)}>Cancel</button>
                    </div>
                  )}

                  {/* Exercise rows — hidden when excluded; shows placeholder when no history */}
                  {!isExcluded && seqs.length === 0 && (
                    <div style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic", padding: "6px 0 2px" }}>
                      No exercises in mapping history for this area yet — hold or remove the part above if needed.
                    </div>
                  )}
                  {!isExcluded && seqs.length > 0 && <div>
                    {seqs.map(s => {
                      const isSel    = selectedEx?.has(s.name) ?? false;
                      const isCommon = commonSeqNames.has(s.name);
                      const held     = isHeld(s.name) || (isPartHeld && isSel);
                      const heldRestartDate = holdData[s.name]?.restartDate
                        || (isPartHeld && isSel ? partHoldState[partKey]?.restartDate : null);
                      const edit = seqEdits[s.name];
                      const dur  = edit?.duration   != null ? edit.duration   : (s.duration  || 60);
                      const rep  = edit?.repetition != null ? edit.repetition : (s.repetition || 1);
                      const showHF = holdForm === s.name || holdForm?.name === s.name;
                      const holdRestartVal = typeof holdForm === "object" && holdForm?.name === s.name ? (holdForm.restartDate || "") : "";
                      const sharedWithLabel = isCommon
                        ? (seqPartsMap[s.name] || []).filter(k => k !== partKey).map(k => PART_LABELS[k] || k).join(", ")
                        : "";
                      // Change-detection badges
                      const isNew     = !initMap[s.name] && isSel;
                      const isRemoved = !!initMap[s.name] && !isSel;
                      const initDur   = initMap[s.name]?.duration   || 60;
                      const initRep   = initMap[s.name]?.repetition || 1;
                      const isEdited  = !!initMap[s.name] && isSel && (dur !== initDur || rep !== initRep);

                      return (
                        <div key={s.name} style={{ borderBottom: "1px solid #f3f4f6", paddingBottom: 5, marginBottom: 4 }}>
                          {/* Main row */}
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            {/* Select/deselect toggle */}
                            <button
                              onClick={() => toggleEx(s.name)}
                              title={isSel ? "Remove from plan" : (isRemoved ? "Re-add to plan" : "Add to plan")}
                              style={{
                                width: 22, height: 22, borderRadius: 5, flexShrink: 0,
                                border: `1.5px solid ${isSel ? color : isRemoved ? "#fecaca" : "#d1d5db"}`,
                                background: isSel ? color : isRemoved ? "#fee2e2" : "#fff",
                                color: isSel ? "#fff" : isRemoved ? "#dc2626" : "#9ca3af",
                                fontSize: 12, cursor: "pointer",
                                display: "flex", alignItems: "center", justifyContent: "center",
                              }}
                            >{isSel ? "✓" : isRemoved ? "↩" : "+"}</button>

                            {/* Name */}
                            <span style={{
                              flex: 1, minWidth: 90, fontSize: 13,
                              fontWeight: isSel ? 600 : 400,
                              color: isSel ? (held ? "#78350f" : color) : isRemoved ? "#dc2626" : "#9ca3af",
                              opacity: isSel ? 1 : 0.65,
                              textDecoration: isRemoved ? "line-through" : "none",
                            }}>
                              {isCommon && <span title={`Also used for: ${sharedWithLabel}`} style={{ marginRight: 3 }}>⚠️</span>}
                              {held && <span style={{ marginRight: 3 }}>🔒</span>}
                              {s.name}
                            </span>
                            {/* State badges */}
                            {isNew     && <span style={{ fontSize: 9, background: "#dcfce7", color: "#16a34a", borderRadius: 99, padding: "1px 6px", border: "1px solid #bbf7d0", fontWeight: 700, whiteSpace: "nowrap" }}>NEW</span>}
                            {isEdited  && <span style={{ fontSize: 9, background: "#fffbeb", color: "#92400e", borderRadius: 99, padding: "1px 6px", border: "1px solid #fde68a", fontWeight: 700, whiteSpace: "nowrap" }}>EDITED</span>}
                            {isRemoved && <span style={{ fontSize: 9, background: "#fee2e2", color: "#dc2626", borderRadius: 99, padding: "1px 6px", border: "1px solid #fecaca", fontWeight: 700, whiteSpace: "nowrap" }}>REMOVED</span>}

                            {/* Duration mm:ss */}
                            <div style={{ display: "flex", alignItems: "center", gap: 1, opacity: isSel ? 1 : 0.35 }}>
                              <input type="number" min="0" max="99" value={Math.floor(dur / 60)}
                                disabled={!isSel}
                                style={{ width: 26, padding: "2px 1px", border: "1px solid #d1d5db", borderRadius: 3, fontSize: 11, textAlign: "center" }}
                                onChange={e => setSeqEdits(prev => ({ ...prev, [s.name]: { duration: Number(e.target.value) * 60 + (dur % 60), repetition: prev[s.name]?.repetition ?? rep } }))}
                              />
                              <span style={{ fontSize: 9, color: "#9ca3af" }}>m:</span>
                              <input type="number" min="0" max="59" value={dur % 60}
                                disabled={!isSel}
                                style={{ width: 26, padding: "2px 1px", border: "1px solid #d1d5db", borderRadius: 3, fontSize: 11, textAlign: "center" }}
                                onChange={e => setSeqEdits(prev => ({ ...prev, [s.name]: { duration: Math.floor(dur / 60) * 60 + Number(e.target.value), repetition: prev[s.name]?.repetition ?? rep } }))}
                              />
                              <span style={{ fontSize: 9, color: "#9ca3af" }}>s</span>
                            </div>

                            {/* Reps */}
                            <div style={{ display: "flex", alignItems: "center", gap: 1, opacity: isSel ? 1 : 0.35 }}>
                              <span style={{ fontSize: 10, color: "#9ca3af" }}>×</span>
                              <input type="number" min="1" max="99" value={rep}
                                disabled={!isSel}
                                style={{ width: 28, padding: "2px 1px", border: "1px solid #d1d5db", borderRadius: 3, fontSize: 11, textAlign: "center" }}
                                onChange={e => setSeqEdits(prev => ({ ...prev, [s.name]: { duration: prev[s.name]?.duration ?? dur, repetition: Number(e.target.value) } }))}
                              />
                            </div>

                            {/* Hold icon */}
                            {isSel && !held && !isPartHeld && canHold && (
                              <button title={showHF ? "Cancel hold" : "Hold"}
                                onClick={() => setHoldForm(showHF ? null : s.name)}
                                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, color: showHF ? "#f59e0b" : "#d1d5db", padding: "0 1px", flexShrink: 0, lineHeight: 1 }}
                              >🔒</button>
                            )}
                            {isSel && held && holdData[s.name] && (
                              <button title="Unhold"
                                onClick={() => unholdSeq(s.name)}
                                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, color: "#16a34a", padding: "0 1px", flexShrink: 0, lineHeight: 1 }}
                              >🔓</button>
                            )}
                            {isSel && held && heldRestartDate && (
                              <span style={{ fontSize: 10, color: "#78350f", background: "#fef3c7", padding: "1px 5px", borderRadius: 99, border: "1px solid #fde68a", whiteSpace: "nowrap" }}>
                                {new Date(heldRestartDate).toLocaleDateString("en-IN", { day:"2-digit", month:"short" })}
                              </span>
                            )}

                            {/* Remove icon */}
                            {isSel && (
                              <button title="Remove"
                                onClick={() => toggleEx(s.name)}
                                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "#dc2626", padding: "0 1px", flexShrink: 0, lineHeight: 1 }}
                              >✕</button>
                            )}
                          </div>

                          {/* Shared note */}
                          {isCommon && isSel && (
                            <div style={{ fontSize: 10, color: "#92400e", marginTop: 2, paddingLeft: 28 }}>
                              ⚠️ Also in: {sharedWithLabel} — duration/reps apply globally
                            </div>
                          )}

                          {/* Hold form */}
                          {showHF && isSel && !held && (
                            <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4, padding: "5px 8px", background: "#fffbeb", borderRadius: 7, border: "1px solid #fde68a", flexWrap: "wrap" }}>
                              <span style={{ fontSize: 10, color: "#92400e" }}>Hold from today</span>
                              <input type="date" value={holdRestartVal}
                                style={{ fontSize: 10, border: "1px solid #fcd34d", borderRadius: 4, padding: "2px 4px" }}
                                onChange={e => setHoldForm({ name: s.name, restartDate: e.target.value })} />
                              <button style={{ fontSize: 10, padding: "2px 8px", borderRadius: 5, border: "none", background: "#f59e0b", color: "#fff", cursor: "pointer", fontWeight: 700 }}
                                onClick={() => holdSeq(s.name, holdRestartVal || null)}>✓ Hold</button>
                              <button style={{ fontSize: 10, padding: "2px 6px", borderRadius: 5, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer" }}
                                onClick={() => setHoldForm(null)}>✕</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>}
                </div>
              );
            })}

            {/* ── Resolved areas (from prev consultation, no longer active) ── */}
            {prevConsultParts && (() => {
              const resolvedParts = [...prevConsultParts].filter(pk => !activeParts.includes(pk));
              if (resolvedParts.length === 0) return null;
              return (
                <div style={{ marginTop: 6, marginBottom: 6, padding: "8px 12px", background: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#16a34a", marginBottom: 4 }}>✅ Resolved Areas — no longer in active treatment</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {resolvedParts.map(pk => (
                      <span key={pk} style={{ fontSize: 11, background: "#dcfce7", borderRadius: 99, padding: "2px 10px", border: "1px solid #bbf7d0", color: "#166534", fontWeight: 600 }}>
                        {PART_LABELS[pk] || pk}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Held exercises summary */}
            {Object.keys(holdData).length > 0 && (
              <div style={{ marginTop: 6, padding: "8px 12px", background: "#fffbeb", borderRadius: 8, border: "1px solid #fde68a" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#92400e", marginBottom: 6 }}>
                  🔒 {Object.keys(holdData).length} exercise(s) on hold — excluded from session time
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {Object.entries(holdData).map(([name, hd]) => (
                    <span key={name} style={{ fontSize: 11, background: "#fef3c7", borderRadius: 99, padding: "2px 9px", border: "1px solid #fde68a", color: "#78350f", display: "flex", alignItems: "center", gap: 4 }}>
                      🔒 {name}
                      {hd.restartDate && <span style={{ fontSize: 10, opacity: 0.8 }}>→ {new Date(hd.restartDate).toLocaleDateString("en-IN", { day:"2-digit", month:"short" })}</span>}
                      <button style={{ background: "none", border: "none", color: "#16a34a", cursor: "pointer", fontSize: 12, padding: "0 0 0 4px" }} onClick={() => unholdSeq(name)}>▶</button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Recommendations ───────────────────────────────────────────── */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Recommendations in Plan ({planRecomms.length})</div>
            {planRecomms.length === 0
              ? <div style={styles.emptyMsg}>No recommendations added yet.</div>
              : <>
                  {/* Column headers */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 4, borderBottom: "1px solid #e5e7eb", marginBottom: 2 }}>
                    <span style={{ flex: 1 }} />
                    <span style={{ width: 52, fontSize: 10, color: "#9ca3af", textAlign: "center" }}>Qty</span>
                    <span style={{ width: 52, fontSize: 10, color: "#9ca3af", textAlign: "center" }}>×/d</span>
                    <span style={{ width: 52, fontSize: 10, color: "#9ca3af", textAlign: "center" }}>Days</span>
                    <span style={{ width: 36 }} />
                  </div>
                  {planRecomms.map(r => {
                    const text = r.recommendation || r.name;
                    const held = !!recHolds[text];
                    return (
                      <div key={text} style={{ ...styles.planRow, opacity: held ? 0.55 : 1 }}>
                        <span style={{ ...styles.planName, textDecoration: held ? "line-through" : "none", color: held ? "#9ca3af" : "#1a202c" }}>
                          {held && <span style={{ marginRight: 3 }}>🔒</span>}{text}
                        </span>
                        <input type="number" min="0" max="9999" placeholder="—" style={{ ...styles.recField, opacity: held ? 0.4 : 1 }}
                          value={r.qty || ""} disabled={held} onChange={e => updateRec(text, "qty", e.target.value)} />
                        <input type="number" min="0" max="9999" placeholder="—" style={{ ...styles.recField, opacity: held ? 0.4 : 1 }}
                          value={r.not || ""} disabled={held} onChange={e => updateRec(text, "not", Number(e.target.value))} />
                        <input type="number" min="0" max="9999" placeholder="—" style={{ ...styles.recField, opacity: held ? 0.4 : 1 }}
                          value={r.nod || ""} disabled={held} onChange={e => updateRec(text, "nod", Number(e.target.value))} />
                        <button title={held ? "Unhold" : "Hold"} onClick={() => toggleRecHold(text)}
                          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: held ? "#f59e0b" : "#d1d5db", padding: "0 1px", flexShrink: 0 }}>🔒</button>
                        <button style={styles.removeBtn} onClick={() => removeRec(text)}>✕</button>
                      </div>
                    );
                  })}
                </>
            }
          </div>

          {/* ── Lab Reading Suggestions (ROM linked parts) ───────────────────── */}
          {romActiveParts.length > 0 && (
            <div style={{ ...styles.section, background: '#f0f9ff', border: '1.5px solid #bae6fd', borderRadius: 10 }}>
              <div style={{ ...styles.sectionTitle, color: '#0369a1' }}>Lab Reading Suggestions</div>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
                Select linked body parts whose ROM readings should also be taken during sessions.
              </div>
              {romActiveParts.map(pk => {
                const partLabel = PART_LABELS[pk] || bpLookup[pk]?.label || pk;
                const linked = bpLookup[pk]?.romLinkedParts || [];
                const selected = romLinkedSelections[pk] || [];
                return (
                  <div key={pk} style={{ marginBottom: 10, padding: '6px 10px', background: '#fff', borderRadius: 8, border: '1px solid #e0f2fe' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#0c4a6e', marginBottom: 4 }}>
                      {partLabel} <span style={{ fontSize: 10, background: '#0ea5e9', color: '#fff', borderRadius: 99, padding: '1px 7px', fontWeight: 600 }}>ROM</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {linked.map(lk => {
                        const lkLabel = PART_LABELS[lk] || bpLookup[lk]?.label || lk;
                        const checked = selected.includes(lk);
                        return (
                          <label key={lk} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer',
                            background: checked ? '#e0f2fe' : '#f8fafc', border: `1px solid ${checked ? '#7dd3fc' : '#e2e8f0'}`,
                            borderRadius: 6, padding: '3px 8px' }}>
                            <input type="checkbox" checked={checked} onChange={() => {
                              setRomLinkedSelections(prev => {
                                const cur = prev[pk] || [];
                                const next = checked ? cur.filter(x => x !== lk) : [...cur, lk];
                                return { ...prev, [pk]: next };
                              });
                            }} />
                            {lkLabel}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Previous GTPs (read-only, add-on mode only) ── */}
          {existingPlan && gtpPlans.length > 0 && (() => {
            const toggleGtp = (n) => setExpandedGtps(prev => {
              const next = new Set(prev);
              next.has(n) ? next.delete(n) : next.add(n);
              return next;
            });
            return (
              <div style={{ ...styles.section, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>Previous GTPs ({gtpPlans.length})</div>
                {gtpPlans.map(gtp => {
                  const totalSess = (gtp.mythsessions || 0) + (gtp.maintsessions || 0) + (gtp.preventivesessions || 0);
                  const svcs = gtp.suggestedServices || [];
                  const isOpen = expandedGtps.has(gtp.gtpNumber);
                  return (
                    <div key={gtp.gtpNumber} style={{ marginBottom: 6 }}>
                      <div onClick={() => toggleGtp(gtp.gtpNumber)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '5px 0', borderBottom: '1px solid #e8ecf4' }}>
                        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600, minWidth: 50 }}>
                          {isOpen ? '▾' : '▸'} GTP {gtp.gtpNumber}
                        </span>
                        <span style={{ fontSize: 10, color: '#94a3b8' }}>
                          {new Date(gtp.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                          {gtp.submittedBy ? ` · ${gtp.submittedBy}` : ''}
                        </span>
                        <span style={{ fontSize: 10, color: totalSess > 0 ? '#7c3aed' : '#94a3b8', fontWeight: 600, marginLeft: 'auto' }}>
                          {totalSess > 0 ? `${totalSess} sessions` : 'Exercises only'}
                        </span>
                        <span style={{
                          fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                          background: gtp.status === 'approved' ? '#dcfce7' : gtp.status === 'rejected' ? '#fef2f2' : '#fef9c3',
                          color: gtp.status === 'approved' ? '#166534' : gtp.status === 'rejected' ? '#991b1b' : '#854d0e',
                        }}>{gtp.status}</span>
                      </div>
                      {isOpen && svcs.length > 0 && (
                        <div style={{ padding: '6px 0 6px 20px' }}>
                          {svcs.map((s, i) => (
                            <div key={i} style={{ fontSize: 11, color: '#475569', display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                              <span>{s.name}</span>
                              <span style={{ color: '#64748b', fontWeight: 600 }}>
                                {s.qty > 0 ? `×${s.qty}` : ''}
                                {s.amount > 0 ? ` · ₹${s.amount}` : ''}
                              </span>
                            </div>
                          ))}
                          {totalSess > 0 && (
                            <div style={{ fontSize: 10, color: '#7c3aed', marginTop: 4, fontWeight: 600 }}>
                              Dr: {gtp.mythsessions || 0} · Sr: {gtp.maintsessions || 0} · Th: {gtp.preventivesessions || 0}
                            </div>
                          )}
                        </div>
                      )}
                      {isOpen && svcs.length === 0 && (
                        <div style={{ padding: '6px 0 6px 20px', fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>No services in this GTP</div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* ── Sessions Plan (add-on to suggestedServices) + Suggested Services ── */}
          {(() => {
            // Map: service name (lowercase) → { addon state, setter, existing qty from DB }
            const SESSION_LINKS = {
              'session with doctor':           { addon: Number(mythsessions) || 0, setAddon: setMyth },
              'session with senior therapist': { addon: Number(maintsessions) || 0, setAddon: setMaint },
              'session with therapist':        { addon: Number(preventivesessions) || 0, setAddon: setPreventive },
            };
            const getExistingQty = (name) =>
              (existingSuggestedServices || []).find(s => (s.name || '').toLowerCase() === (name || '').toLowerCase())?.qty || 0;

            // Unique orgs from paid services (amount > 0)
            const uniqueOrgs = [...new Set(
              allServices.filter(s => (s.amount || 0) > 0 && s.org).map(s => s.org)
            )];
            const toggleOrg = (org) => setSelectedOrgs(prev =>
              prev.includes(org) ? prev.filter(o => o !== org) : [...prev, org]
            );

            // Consultation preview service (shown when NOT in review mode)
            const consultSvcName = consultDoctor === 'deepak'
              ? 'Consultation with Dr. Deepak'
              : 'Consultation with Doctor';
            const consultSvc = allServices.find(s => (s.name || '').toLowerCase() === consultSvcName.toLowerCase());
            const consultAmt = consultSvc?.amount || 0;

            // Grand total: session add-ons + other services + consultation preview (if not review)
            const grandTotal = planServices.reduce((sum, svc) => {
              const nameLower = (svc.name || '').toLowerCase();
              const link = SESSION_LINKS[nameLower];
              const billingQty = link ? (link.addon || 0) : (Number(svc.qty) || 0);
              return sum + billingQty * (Number(svc.amount) || 0);
            }, 0) + (!isReview ? consultAmt : 0);

            return (
              <div style={styles.section}>
                {/* ── Consultation mode: compact 3-option segmented strip ── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, flexShrink: 0 }}>Consultation:</span>
                  <div style={{ display: 'flex' }}>
                    {[
                      { value: 'review', label: 'Review' },
                      { value: 'doctor', label: 'Doctor' },
                      { value: 'deepak', label: 'Dr. Deepak' },
                    ].map((opt, i, arr) => {
                      const active = consultMode === opt.value;
                      return (
                        <button key={opt.value} type="button" onClick={() => setConsultMode(opt.value)}
                          style={{
                            padding: '3px 10px', fontSize: 11, fontWeight: active ? 700 : 400,
                            border: '1px solid ' + (active ? '#2563eb' : '#d1d5db'),
                            background: active ? (opt.value === 'review' ? '#f1f5f9' : '#dbeafe') : '#fff',
                            color: active ? (opt.value === 'review' ? '#374151' : '#1d4ed8') : '#6b7280',
                            cursor: 'pointer',
                            borderRadius: i === 0 ? '6px 0 0 6px' : i === arr.length - 1 ? '0 6px 6px 0' : '0',
                            marginLeft: i === 0 ? 0 : -1,
                            position: 'relative', zIndex: active ? 1 : 0,
                          }}>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  {!isReview && (
                    <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>
                      + {consultSvcName}{consultAmt > 0 ? ` ₹${consultAmt}` : ''}
                    </span>
                  )}
                </div>

                {/* ── Sessions / Packages — direct editable counts ── */}
                <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
                  background: "#f0f4ff", border: "1px solid #c7d2fe", borderRadius: 8, padding: "8px 14px", marginBottom: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#3730a3", flexShrink: 0 }}>Sessions</span>
                  {[
                    { label: 'Doctor', value: mythsessions, set: setMyth },
                    { label: 'Sr. Therapist', value: maintsessions, set: setMaint },
                    { label: 'Therapist', value: preventivesessions, set: setPreventive },
                  ].map(s => (
                    <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ fontSize: 11, color: "#4338ca" }}>{s.label}</span>
                      <input type="number" min="0" max="999"
                        style={{ width: 44, padding: "3px 4px", border: "1px solid #a5b4fc", borderRadius: 6,
                          fontSize: 14, fontWeight: 700, textAlign: "center", color: "#4f46e5", background: "#fff" }}
                        value={s.value === 0 ? "" : s.value}
                        placeholder="0"
                        onChange={e => s.set(e.target.value)} />
                    </div>
                  ))}
                  <span style={{ fontSize: 11, color: "#6366f1", fontWeight: 600 }}>
                    Total: {(Number(mythsessions) || 0) + (Number(maintsessions) || 0) + (Number(preventivesessions) || 0)}
                  </span>
                  {existingPlan && (
                    <span style={{ fontSize: 10, color: "#6b7280" }}>
                      (prev: {(existingPlan.mythsessions || 0) + (existingPlan.maintsessions || 0) + (existingPlan.preventivesessions || 0)}
                      {completedSessionsCount > 0 && ` · ${completedSessionsCount} done`})
                    </span>
                  )}
                </div>

                {/* Header row: title + completion badge + Add button */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                  <div style={styles.sectionTitle}>Suggested Services</div>
                  {existingPlan && (
                    <span style={{ fontSize: 11, background: "#dbeafe", color: "#1e40af", borderRadius: 99, padding: "2px 9px", fontWeight: 600 }}>
                      Prev: {(existingPlan.mythsessions || 0) + (existingPlan.maintsessions || 0) + (existingPlan.preventivesessions || 0)} planned
                      {completedSessionsCount > 0 && ` · ${completedSessionsCount} done`}
                    </span>
                  )}
                  <button
                    style={{ marginLeft: "auto", fontSize: 11, padding: "2px 10px", borderRadius: 99, border: "1px solid #6366f1",
                      background: "#eef2ff", color: "#4f46e5", cursor: "pointer", fontWeight: 600 }}
                    onClick={() => setShowSvcPicker(v => !v)}>
                    {showSvcPicker ? "✕ Close" : "+ Add Service"}
                  </button>
                </div>

                {/* Add Service picker with org filters */}
                {showSvcPicker && (
                  <div style={{ marginBottom: 10 }}>
                    {uniqueOrgs.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
                        {uniqueOrgs.map(org => {
                          const active = selectedOrgs.length === 0 || selectedOrgs.includes(org);
                          return (
                            <button key={org} onClick={() => toggleOrg(org)}
                              style={{ fontSize: 10, padding: "2px 8px", borderRadius: 99, cursor: "pointer", border: "1px solid",
                                borderColor: selectedOrgs.includes(org) ? "#4f46e5" : "#d1d5db",
                                background: selectedOrgs.includes(org) ? "#eef2ff" : "#f9fafb",
                                color: selectedOrgs.includes(org) ? "#4f46e5" : "#6b7280", fontWeight: selectedOrgs.includes(org) ? 600 : 400,
                              }}>
                              {org}
                            </button>
                          );
                        })}
                        {selectedOrgs.length > 0 && (
                          <button onClick={() => setSelectedOrgs([])}
                            style={{ fontSize: 10, padding: "2px 6px", borderRadius: 99, cursor: "pointer",
                              border: "1px solid #d1d5db", background: "#fff", color: "#6b7280" }}>
                            Clear
                          </button>
                        )}
                      </div>
                    )}
                    <input type="text" placeholder="Search services…" value={svcSearch}
                      onChange={e => setSvcSearch(e.target.value)}
                      style={{ width: "100%", padding: "6px 10px", border: "1px solid #d1d5db",
                        borderRadius: 7, fontSize: 12, boxSizing: "border-box" }} />
                    <div style={{ maxHeight: 160, overflowY: "auto", border: "1px solid #e5e7eb",
                      borderRadius: 6, marginTop: 4, background: "#fff" }}>
                      {(() => {
                        const candidates = allServices
                          .filter(s => (s.amount || 0) > 0)
                          .filter(s => selectedOrgs.length === 0 || selectedOrgs.includes(s.org))
                          .filter(s => !planServices.some(p => p.name === s.name))
                          .filter(s => !svcSearch || (s.name || '').toLowerCase().includes(svcSearch.toLowerCase()))
                          .slice(0, 30);
                        return candidates.length ? candidates.map(s => (
                          <div key={s._id || s.name} onClick={() => addSvc(s)}
                            style={{ padding: "6px 10px", fontSize: 12, cursor: "pointer",
                              borderBottom: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span>{s.name} <span style={{ fontSize: 10, color: "#9ca3af" }}>{s.org}</span></span>
                            <span style={{ color: "#6b7280", flexShrink: 0 }}>₹{s.amount}</span>
                          </div>
                        )) : (
                          <div style={{ padding: "8px 10px", fontSize: 12, color: "#9ca3af" }}>
                            {allServices.filter(s => (s.amount || 0) > 0).length === 0 ? "Loading…" : "No services match"}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Column headers */}
                {(planServices.length > 0 || !isReview) && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4, paddingBottom: 4, borderBottom: "1px solid #e5e7eb", marginBottom: 2 }}>
                    <span style={{ flex: 1, fontSize: 10, color: "#9ca3af" }}>Service</span>
                    <span style={{ fontSize: 10, color: "#9ca3af", textAlign: "right", marginRight: 4 }}>Existing / New</span>
                    <span style={{ width: 56, fontSize: 10, color: "#9ca3af", textAlign: "right" }}>Rate</span>
                    <span style={{ width: 64, fontSize: 10, color: "#9ca3af", textAlign: "right" }}>New Amt</span>
                    <span style={{ width: 20 }} />
                  </div>
                )}

                {planServices.length === 0 && (
                  <div style={styles.emptyMsg}>No services added — click "+ Add Service" or session-linked rows appear automatically.</div>
                )}

                {planServices.map((svc, idx) => {
                  const nameLower = (svc.name || '').toLowerCase();
                  const link = SESSION_LINKS[nameLower];
                  const prevQty = getExistingQty(svc.name);
                  const addon = link ? (link.addon ?? 0) : 0;
                  const billingQty = link ? addon : (Number(svc.qty) || 0);
                  const amt = Number(svc.amount) || 0;
                  const isLinkedSvc = !!link;
                  const isPkg = _isPkg(svc);
                  return (
                    <div key={idx} style={{ ...styles.planRow, background: isLinkedSvc ? "#f8fafc" : "transparent",
                      borderRadius: isLinkedSvc ? 6 : 0, padding: "5px 4px", marginBottom: 1 }}>
                      <span style={{ flex: 1, fontSize: 12, color: "#1a202c", fontWeight: isLinkedSvc ? 600 : 400,
                        minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {svc.name}
                        {isPkg && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: '#7c3aed', background: '#ede9fe', padding: '1px 6px', borderRadius: 4 }}>{svc._masterQty} sessions/pkg</span>}
                      </span>

                      {isLinkedSvc ? (
                        /* Two visually distinct blocks: EXISTING (grey, static) | NEW (indigo, editable) */
                        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                          {/* EXISTING block — only show if there's a previous plan */}
                          {existingPlan && (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center",
                              background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 6, padding: "2px 8px", minWidth: 44 }}>
                              <span style={{ fontSize: 8, color: "#9ca3af", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>Existing</span>
                              <span style={{ fontSize: 15, fontWeight: 700, color: "#6b7280", lineHeight: 1.2 }}>{prevQty}</span>
                            </div>
                          )}
                          {existingPlan && <span style={{ fontSize: 11, color: "#d1d5db" }}>+</span>}
                          {/* NEW block — editable */}
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center",
                            background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 6, padding: "2px 6px", minWidth: 44 }}>
                            <span style={{ fontSize: 8, color: "#6366f1", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>New</span>
                            <input type="number" min="0" max="9999"
                              style={{ width: 36, fontSize: 15, fontWeight: 700, color: "#4f46e5", lineHeight: 1.2,
                                border: "none", background: "transparent", textAlign: "center", outline: "none", padding: 0 }}
                              value={addon === 0 ? "" : addon}
                              placeholder="0"
                              onChange={e => link.setAddon(e.target.value)} />
                          </div>
                        </div>
                      ) : (
                        /* Non-session services: plain qty input */
                        <input type="number" min="0" max="9999" style={{ ...styles.recField, width: 44, flexShrink: 0 }}
                          value={billingQty || ""} onChange={e => updateSvcQty(idx, e.target.value)} />
                      )}

                      <span style={{ width: 56, fontSize: 11, color: "#6b7280", textAlign: "right", flexShrink: 0 }}>
                        {amt > 0 ? `₹${amt}` : "—"}
                      </span>
                      <span style={{ width: 64, fontSize: 11, color: isLinkedSvc ? "#4f46e5" : "#1a202c", textAlign: "right", fontWeight: 600, flexShrink: 0 }}>
                        {amt > 0 && billingQty > 0 ? `₹${(amt * billingQty).toLocaleString("en-IN")}` : "—"}
                      </span>
                      <button style={{ ...styles.removeBtn, width: 20 }} onClick={() => removeSvc(idx)}>✕</button>
                    </div>
                  );
                })}

                {/* Consultation service preview row — shown when NOT in review mode */}
                {!isReview && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 4px", marginBottom: 1,
                    background: "#f0fdf4", borderRadius: 6, border: "1px dashed #86efac" }}>
                    <span style={{ flex: 1, fontSize: 12, color: "#166534", fontWeight: 600,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {consultSvcName}
                    </span>
                    {/* New badge — qty 1, non-editable */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center",
                      background: "#dcfce7", border: "1px solid #86efac", borderRadius: 6,
                      padding: "2px 8px", minWidth: 44, flexShrink: 0 }}>
                      <span style={{ fontSize: 8, color: "#16a34a", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>New</span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: "#166534", lineHeight: 1.2 }}>1</span>
                    </div>
                    <span style={{ width: 56, fontSize: 11, color: "#6b7280", textAlign: "right", flexShrink: 0 }}>
                      {consultAmt > 0 ? `₹${consultAmt}` : "—"}
                    </span>
                    <span style={{ width: 64, fontSize: 11, color: "#166534", textAlign: "right", fontWeight: 600, flexShrink: 0 }}>
                      {consultAmt > 0 ? `₹${consultAmt.toLocaleString("en-IN")}` : "—"}
                    </span>
                    <span style={{ width: 20, flexShrink: 0 }} />
                  </div>
                )}

                {/* Grand total — new sessions only */}
                {grandTotal > 0 && (
                  <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "baseline", gap: 6,
                    paddingTop: 6, borderTop: "1px solid #e5e7eb", marginTop: 4 }}>
                    <span style={{ fontSize: 10, color: "#6b7280" }}>New sessions total</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#4f46e5" }}>
                      ₹{grandTotal.toLocaleString("en-IN")}
                    </span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Doctor comments ───────────────────────────────────────────── */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Doctor Comments</div>
            <textarea
              style={styles.textarea}
              placeholder="Enter clinical notes, special instructions…"
              value={drcomments}
              onChange={e => setDrcomments(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button style={styles.cancelBtn} onClick={onClose} disabled={submitting}>Cancel</button>
          <button style={styles.submitBtn} onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Submitting…" : isReview ? "Submit (Review)" : "Submit for Approval"}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 3000, padding: 16,
  },
  modal: {
    background: "#fff", borderRadius: 14, width: "min(900px, 97vw)",
    maxHeight: "92vh", display: "flex", flexDirection: "column",
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
  },
  header: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    padding: "18px 22px 14px", borderBottom: "1px solid #e8e8e8", flexShrink: 0,
  },
  title: { fontSize: 17, fontWeight: 700, color: "#1a202c" },
  subtitle: { fontSize: 12, color: "#666", marginTop: 2 },
  closeBtn: {
    background: "none", border: "none", fontSize: 18, cursor: "pointer",
    color: "#888", padding: "0 0 0 16px", flexShrink: 0,
  },
  body: { overflowY: "auto", flex: 1, padding: "0 0 4px" },
  loadingMsg: { padding: "16px 22px", color: "#666", fontSize: 13, fontStyle: "italic" },
  section: { padding: "14px 22px 10px", borderBottom: "1px solid #f0f0f0" },
  sectionTitle: {
    fontSize: 13, fontWeight: 700, color: "#2d3748",
    marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em",
  },
  sessRow: { display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "nowrap", paddingBottom: 8 },
  sessLabel: { display: "flex", flexDirection: "column", gap: 3, fontSize: 11, color: "#555" },
  sessInput: {
    width: 44, padding: "4px 6px", border: "1px solid #ccc",
    borderRadius: 6, fontSize: 13, textAlign: "center",
    MozAppearance: "textfield", appearance: "textfield",
  },
  sessTotal: { fontSize: 13, color: "#444", marginLeft: "auto" },
  painBadge: {
    fontSize: 11, background: "#fee2e2", color: "#c0392b",
    borderRadius: 99, padding: "1px 7px", fontWeight: 600,
  },
  planRow: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "5px 0", borderBottom: "1px solid #f5f5f5",
  },
  planName: { flex: 1, fontSize: 13, color: "#1a202c" },
  recField: {
    width: 52, padding: "3px 4px", border: "1px solid #d1d5db",
    borderRadius: 5, fontSize: 11, textAlign: "center",
    MozAppearance: "textfield", appearance: "textfield",
  },
  removeBtn: {
    background: "none", border: "none", color: "#e53e3e",
    cursor: "pointer", fontSize: 14, padding: "0 4px", flexShrink: 0,
  },
  emptyMsg: { fontSize: 12, color: "#aaa", fontStyle: "italic", paddingBottom: 8 },
  textarea: {
    width: "100%", padding: "8px 10px", border: "1px solid #d1d5db",
    borderRadius: 8, fontSize: 13, resize: "vertical", fontFamily: "inherit",
    boxSizing: "border-box",
  },
  footer: {
    display: "flex", justifyContent: "flex-end", gap: 12,
    padding: "14px 22px", borderTop: "1px solid #e8e8e8", flexShrink: 0,
  },
  cancelBtn: {
    padding: "9px 22px", borderRadius: 8, border: "1px solid #ccc",
    background: "#fff", color: "#555", fontSize: 14, cursor: "pointer",
  },
  submitBtn: {
    padding: "9px 22px", borderRadius: 8, border: "none",
    background: "#4f46e5", color: "#fff", fontSize: 14,
    cursor: "pointer", fontWeight: 600,
  },
  smallBtn: {
    fontSize: 11, padding: "2px 8px", border: "1px solid #d1d5db",
    borderRadius: 5, cursor: "pointer", background: "#fff", color: "#555",
  },
};
